import type { Configuration } from "@azure/msal-browser";
import { LogLevel } from "@azure/msal-browser";

/**
 * The client ID is public by design — it is not a secret and ships in the bundle.
 * See ARCHITECTURE.md §12: nothing else about the app registration may live here.
 *
 * Overridable via VITE_MSAL_CLIENT_ID so a fork can point at its own registration
 * without editing source. The committed default keeps the Cloudflare Pages build
 * working with no dashboard configuration.
 */
export const CLIENT_ID: string =
    import.meta.env.VITE_MSAL_CLIENT_ID ?? "7988b73b-00b8-46ae-91ee-b5d92db1454d";

/**
 * Multitenant, organizational accounts only — matches signInAudience
 * "AzureADMultipleOrgs" on the registration (ARCHITECTURE.md §4).
 */
export const AUTHORITY = "https://login.microsoftonline.com/organizations";

/**
 * Points at the auth bridge page, never at the app root.
 *
 * MSAL relays a popup's response to the opener from whatever page the redirect
 * lands on. If that page is the app itself, the entire SPA boots a second time
 * inside the auth popup — a duplicate UI whose sign-in button throws
 * block_nested_popups. auth.html exists solely to do the relaying.
 *
 * Derived from the origin so dev (http://localhost:5173) and production
 * (https://o365.qinbot.fr) both work from one build. Both must be registered
 * under the registration's **spa** platform, including the /auth.html path.
 * Registering under **web** instead produces `cross-origin_token_redemption`
 * at runtime (ARCHITECTURE.md §4).
 */
export const REDIRECT_URI = `${window.location.origin}/auth.html`;

export const msalConfig: Configuration = {
    auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: REDIRECT_URI,
        // Also the bridge: logoutPopup lands here too, and MSAL treats server
        // logout as best-effort, so a failure here cannot strand the user.
        postLogoutRedirectUri: REDIRECT_URI
    },
    cache: {
        // localStorage, not the MSAL default of sessionStorage: an export run
        // outlives a tab close, and resume depends on the account surviving with it.
        cacheLocation: "localStorage"
    },
    system: {
        loggerOptions: {
            logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
            piiLoggingEnabled: false,
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                if (level === LogLevel.Error) {
                    console.error(message);
                } else if (level === LogLevel.Warning) {
                    console.warn(message);
                }
            }
        }
    }
};

/**
 * Sign-in requests the minimum only. Module scopes are requested when the user
 * selects that module, never up front (ARCHITECTURE.md §4, Appendix A item 6).
 *
 * openid / profile / offline_access are added by MSAL automatically and are
 * deliberately not listed here.
 */
export const LOGIN_SCOPES = ["email", "User.Read"] as const;

export type ModuleId =
    | "mail"
    | "calendar"
    | "contacts"
    | "tasks"
    | "onenote"
    | "teams"
    | "onedrive";

/**
 * The 16-scope set from ARCHITECTURE.md §3, split per module.
 *
 * Do not add a scope here without agreement — the whole design depends on every
 * scope staying inside the no-admin-consent set (Claude.md, non-negotiables).
 */
export const MODULE_SCOPES: Record<ModuleId, readonly string[]> = {
    mail: ["Mail.Read", "Mail.Read.Shared", "MailboxSettings.Read"],
    calendar: ["Calendars.Read", "Calendars.Read.Shared"],
    contacts: ["Contacts.Read", "Contacts.Read.Shared"],
    tasks: ["Tasks.Read"],
    onenote: ["Notes.Read.All"],
    teams: ["Chat.Read"],
    onedrive: ["Files.Read"]
};

/**
 * Write scopes for the optional "delete from Microsoft" action (Teams and Mail
 * only). Kept separate from the read scopes above so the app stays read-only
 * unless the user deliberately deletes something — these are requested (and
 * consented to) only on the first delete, never at sign-in or export.
 *
 * Both are delegated and user-consentable (no admin consent). They must also
 * be added to the app registration's delegated permissions to be grantable.
 */
export const DELETE_SCOPES = {
    teams: ["Chat.ReadWrite"],
    mail: ["Mail.ReadWrite"]
} as const satisfies Partial<Record<ModuleId, readonly string[]>>;

export type DeletableModuleId = keyof typeof DELETE_SCOPES;

export const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
